'use client';

import { datadogRum } from '@datadog/browser-rum';
import { logger } from '@magicschool/logger';
import type { AuthChangeEvent, Session, User } from '@supabase/supabase-js';
import { useEffect } from 'react';
import { useCookieYesConsent } from '@/components/CookieYes/useCookieYesConsent';
import { clearDatadogUser, setDatadogUser } from '@/lib/observability/rum';
import { deleteCookie } from '@/util/cookies';
import { clientSupabase } from '@/util/supabase';

/**
 * Set user context from auth session data only (no DB calls)
 */
const setUserFromAuthData = (authUser: User) => {
  if (!authUser?.id) return;

  // Create minimal user object from auth session data
  const minimalUser = {
    id: authUser.id,
    email: authUser.email,
    // Use metadata if available, otherwise defaults
    user_role: authUser.user_metadata?.user_role || authUser.app_metadata?.user_role || 'teacher',
    plan: authUser.user_metadata?.plan || authUser.app_metadata?.plan || 1, // Keep as number - setDatadogUser converts to name
    is_demo: authUser.user_metadata?.is_demo || authUser.app_metadata?.is_demo || false,
  };

  setDatadogUser(minimalUser);
  logger.debug('[Datadog] User context set from auth data', {
    userId: minimalUser.id,
    hasMetadata: !!(authUser.user_metadata || authUser.app_metadata),
  });
};

/**
 * Set initial user context if already authenticated (optimized with getSession)
 */
const setInitialUser = async () => {
  try {
    const {
      data: { session },
    } = await clientSupabase.auth.getSession();
    if (session?.user) {
      setUserFromAuthData(session.user);
    }
  } catch (error) {
    logger.warn('[Datadog] Failed to set initial user context', { error });
  }
};

export interface DatadogUserContextProps {
  /** True for a student session — hard-gates Datadog RUM tracking consent, independent of CookieYes's consent state. */
  isStudent?: boolean;
}

/**
 * Client component that automatically syncs Supabase user auth state with Datadog RUM/Logs
 * Uses only auth session data to avoid extra database calls while providing user identification
 */
export function DatadogUserContext({ isStudent = false }: DatadogUserContextProps) {
  const consent = useCookieYesConsent();
  const analyticsGranted = !isStudent && consent.analytics;

  useEffect(() => {
    if (analyticsGranted) {
      datadogRum.setTrackingConsent('granted');
    } else {
      datadogRum.setTrackingConsent('not-granted');
      deleteCookie('_dd_s');
    }
  }, [analyticsGranted]);

  useEffect(() => {
    const {
      data: { subscription },
    } = clientSupabase.auth.onAuthStateChange((event: AuthChangeEvent, session: Session | null) => {
      try {
        if (event === 'SIGNED_IN' && session?.user) {
          // User logged in - set Datadog context from session data
          setUserFromAuthData(session.user);
          logger.debug('[Datadog] User context set on login', {
            userId: session.user.id,
            event,
          });
        } else if (event === 'SIGNED_OUT') {
          // User logged out - clear Datadog context
          clearDatadogUser();
          logger.debug('[Datadog] User context cleared on logout', { event });
        }
      } catch (error) {
        logger.error('[Datadog] Failed to update user context', {
          error: error instanceof Error ? error.message : String(error),
          event,
        });
      }
    });

    // Set initial user context
    setInitialUser();

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // This component doesn't render anything - it's just for side effects
  return null;
}
